import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import dayjs from 'common/dayjs';
import { getHostName } from 'app/global';
import { environment } from '../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';

@Injectable()
export class TokenStorageService {
  static token: ReplaySubject<any> = new ReplaySubject(1);
  private _userToken;
  private _tempToken;
  private _mfaToken;
  private _flow;
  private _tokenExpiray;
  private _redirectUrl;

  get dataStorage() {
    const disabledCookiesUrl = '/disabled-cookies';
    if (window.navigator.cookieEnabled) {
      return {
        setItem: (name = '', value = '', domain: string = getHostName(), path = '/', expires?) => {
          this.cookieService.set(name, value, expires, path, domain);
        },
        removeItem: (name = '', domain = getHostName(), path = '/') => {
          this.cookieService.delete(name, path, domain);
          this.cookieService.delete(name, path, window.location.hostname);
          if (environment.name.toLowerCase() === 'production') {
            this.cookieService.delete(name, path, 'app.certemy.com');
          }
        },
        getItem: (name = '') => {
          return this.cookieService.get(name);
        },
      };
    } else {
      if (window.location.pathname !== disabledCookiesUrl) {
        window.location.href = `${window.location.protocol}//${window.location.host}${disabledCookiesUrl}`;
      }
      return {
        setItem: (_name, _value) => undefined,
        getItem: (name) => '',
        removeItem: (_name) => undefined,
      };
    }
  }

  constructor(private cookieService: CookieService) {
    this._tokenExpiray = environment.JWT_EXPIRE_TIME;
  }

  get userToken() {
    return this._userToken ? this._userToken : (this._userToken = this.dataStorage.getItem('userToken'));
  }

  set userToken(userToken: string) {
    this._userToken = userToken;
    this.dataStorage.setItem('userToken', userToken);
    this.dataStorage.setItem('userTokenTimeStamp', JSON.stringify(Date.now()));
  }

  get tempToken() {
    return this._tempToken ? this._tempToken : (this._tempToken = this.dataStorage.getItem('tempToken'));
  }

  set tempToken(tempToken: string) {
    this._tempToken = tempToken;
    this.dataStorage.setItem('tempToken', tempToken);
  }

  get mfaToken() {
    return this._mfaToken ? this._mfaToken : (this._mfaToken = this.dataStorage.getItem('mfaToken'));
  }

  set mfaToken(mfaToken: string) {
    this._mfaToken = mfaToken;
    this.dataStorage.setItem('mfaToken', mfaToken);
  }

  get flow() {
    return this._flow ? this._flow : (this._flow = this.dataStorage.getItem('flow'));
  }

  set flow(flow: string) {
    this._flow = flow;
    this.dataStorage.setItem('flow', flow);
  }

  get tokenExpiray() {
    return this._tokenExpiray ? this._tokenExpiray : (this._tokenExpiray = this.dataStorage.getItem('tokenExpiray'));
  }

  set tokenExpiray(expiray) {
    this._tokenExpiray = expiray;
    this.dataStorage.setItem('tokenExpiray', expiray);
  }

  get redirectUrl() {
    return this._redirectUrl;
  }

  set redirectUrl(redirectUrl) {
    this._redirectUrl = redirectUrl;
  }

  get isLoggedIn(): boolean {
    return !!this.userToken && !this.isTokenExpired();
  }

  dropUserToken() {
    this._userToken = '';
    this.dataStorage.removeItem('userToken');
    this.dataStorage.removeItem('userTokenTimeStamp');
  }

  dropTempToken() {
    this._tempToken = '';
    this._flow = '';
    this.dataStorage.removeItem('tempToken');
    this.dataStorage.removeItem('flow');
  }

  dropMfaToken() {
    this._mfaToken = '';
    this.dataStorage.removeItem('mfaToken');
  }

  dropAllTokens() {
    this.dropUserToken();
    this.dropMfaToken();
  }

  dropRedirectUrl() {
    this._redirectUrl = '';
  }

  isTokenExpired(offset = 0) {
    const timeStampValue = this.dataStorage.getItem('userTokenTimeStamp');

    if (timeStampValue) {
      const timeStamp = dayjs(+timeStampValue);
      const timePassed = dayjs().diff(timeStamp, 'minutes');
      const expirationTime = Number(this.tokenExpiray - offset);

      return timePassed >= expirationTime;
    }

    return false;
  }

  replaceUserToken(token) {
    this.userToken = token;
  }
}
